import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {formatCurrency, formatDate} from '@angular/common';
import {Subscription} from 'rxjs';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {
  AdminDashboardNonClientCompanies,
  AdminDashboardNonClientCompany,
  AdminDashboardQueryParams,
  AdminDashboardScope,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {
  AdminDashboardReportBlock,
  AdminDashboardReportProvider,
  AdminDashboardReportRow
} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';

/**
 * "Not in Client Manager": in the Non-clients scope only, the non-client companies of the latest daily snapshot,
 * most recently active first — the candidates worth adding to Client Manager. Shows at most 100 rows (the server's
 * cap) with a "Showing 100 of N" footer when truncated. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-nonclient-list',
  templateUrl: './admin-dashboard-nonclient-list.component.html',
  styleUrls: ['./admin-dashboard-nonclient-list.component.scss']
})
export class AdminDashboardNonClientListComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  /** Package chips shown per row before collapsing the rest into "+N". */
  readonly maxChips = 3;

  @Input() scope: AdminDashboardScope = 'Clients';
  @Input() params: AdminDashboardQueryParams;
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  rows: AdminDashboardNonClientCompany[] = [];
  total = 0;
  loaded = false;
  /** The last load failed: the list says so instead of looking empty. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load the companies not in Client Manager';

  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>) {}

  get isNonClients(): boolean {
    return this.scope === 'NonClients';
  }

  get truncated(): boolean {
    return this.total > this.rows.length;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params'] || changes['scope']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('attention', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-08). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.isNonClients || !this.loaded) {
      return [];
    }
    const title = 'Not in Client Manager';
    if (this.failed) {
      return [{kind: 'empty', title, text: this.failedTitle}];
    }
    const subtitle = 'most recently active first';
    if (!this.rows.length) {
      return [{kind: 'empty', title, subtitle, text: 'No non-client companies yet'}];
    }
    const block: AdminDashboardReportBlock = {
      kind: 'table',
      title,
      subtitle,
      columns: [
        {header: 'Company', weight: 2},
        {header: 'Source', weight: 1.2},
        {header: 'Active users', align: 'right'},
        {header: 'Packages', weight: 1.6},
        {header: 'Last login'},
        {header: 'XatBot spend (30d)', align: 'right', weight: 1.2}
      ],
      rows: this.rows.map((row): AdminDashboardReportRow => {
        const codes = row.packageCodes ?? [];
        const more = codes.length > this.maxChips ? ` +${codes.length - this.maxChips}` : '';
        return {
          cells: [
            row.companyName,
            row.source || '—',
            String(row.activeUsers),
            codes.slice(0, this.maxChips).join(', ') + more,
            row.lastLogin ? formatDate(row.lastLogin, 'd MMM y', 'en-US', 'UTC') : 'Never',
            formatCurrency(row.spend30, 'en-US', '$', 'USD', '1.2-2')
          ]
        };
      })
    };
    if (this.truncated) {
      block.note = `Showing ${this.rows.length} of ${this.total}`;
    }
    return [block];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params || !this.isNonClients) {
      this.rows = [];
      this.total = 0;
      this.loaded = false;
      this.failed = false;
      // The list shows nothing on Clients.
      this.report('empty');
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getNonClientCompanies(this.params).subscribe({
      next: list => this.show(list, false),
      error: () => this.show(null, true)
    });
  }

  private show(list: AdminDashboardNonClientCompanies | null, failed: boolean): void {
    this.rows = Array.isArray(list?.Rows) ? list.Rows : [];
    this.total = typeof list?.Total === 'number' ? list.Total : this.rows.length;
    this.failed = failed;
    this.loaded = true;
    this.report(failed ? 'failed' : this.rows.length ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
